import ServerData from './ServerData';
import Uri from '@ms/odsp-utilities/lib/uri/Uri';
import Scope from '@ms/odsp-utilities/lib/scope/Scope';
import APICallPerformanceData from '@ms/odsp-utilities/lib/logging/rumone/APICallPerformanceData';
import { RUMOneLogger } from '@ms/odsp-utilities/lib/logging/rumone/RUMOneLogger';
import { Engagement } from '@ms/odsp-utilities/lib/logging/events/Engagement.event';
import EventGroup from '@ms/odsp-utilities/lib/events/EventGroup';
import Promise from '@ms/odsp-utilities/lib/async/Promise';
import { IDisposable } from '@ms/odsp-utilities/lib/interfaces/IDisposable';
import { Killswitch } from '@ms/odsp-utilities/lib/killswitch/Killswitch';
import { PlatformDetection } from '@ms/utilities-browser/lib/PlatformDetection';
import { ApiError } from '@ms/utilities-error/lib/ApiError';

export interface IFormDigest {
    /**
     * formDigest serverTime. Populated either from pageContext.serverTime
     * or from the response time of _api/contextinfo call. Will be used
     * in a new logic to check formDigest expiration. In standard ISO format
     */
    serverTime?: string;
    /**
     * The time at which digest information was provided on the page.
     * If present, this may allow the connection to avoid requesting digest information
     * before making the request.
     */
    updateFormDigestPageLoaded?: Date;
    /** The form digest value used to validate postback requests. */
    formDigestValue?: string;
    /**
     * The time interval in seconds to the form digest expiration.
     * To be used in conjunction with the updateFormDigestPageLoaded/serverTime to compute the actual expiration time.
     */
    formDigestTimeoutSeconds?: number;
}

export interface IServerConnectionParams {
    /** If provided, this will be used for requests, and _alwaysGetDigest will be set to true. */
    webAbsoluteUrl?: string;
    /** Only used if webAbsoluteUrl is not provided. */
    webServerRelativeUrl?: string;
    needsRequestDigest?: boolean;
    /** Deprecated. Use webAbsoluteUrl. */
    webUrl?: string;
    formDigest?: IFormDigest;
    engagement?: typeof Engagement;
    operationName?: string;
}

export interface IFailureCallback {
    (serverData?: ServerData, error?: Error): any;
}

export interface IGetServerDataFromUrlOptions {
    url: string;
    successCallback: (serverData: ServerData) => any;
    failureCallback: IFailureCallback;
    uploadProgressCallback?: (event: ProgressEvent) => void;
    additionalPostData?: string | Blob;
    withCredentials?: boolean;
    /** Whether this is a REST request. Default true. */
    isRest?: boolean;
    method: string;
    additionalHeaders?: {
        [key: string]: string;
    };
    contentType?: string;
    noRedirect?: boolean;
    responseType?: string;
    needsRequestDigest?: boolean;
}

type ExtendedXHR = XMLHttpRequest & {
    isCancelled?: boolean;
};

// Seconds subtracted from request digest interval to compensate for client-server delay
const CLIENT_SERVER_BUFFER = 60;

export default class ServerConnection implements IDisposable {
    private static _formDigests: { [webServerRelativeUrl: string]: IFormDigest } = {};
    private static _requestDigestPromise: Promise<string> = undefined;

    private _events: EventGroup;
    private _currentRequest: ExtendedXHR;
    private _needsRequestDigest: boolean;
    private _requestCanaryForAuth: boolean;
    private _alwaysGetDigest: boolean;
    private _webAbsoluteUrl: string;
    private _webServerRelativeUrl: string;
    private _scope: Scope;
    private _engagement: typeof Engagement;
    private _operationName: string;

    private static _useServerTimeExpirationCheck(): boolean {
        const isMagellan: boolean = new PlatformDetection().isMagellan;
        const useServerTimeExpirationCheckKillswitch: boolean = !Killswitch.isActivated('561C4018-2C0E-4580-9779-F755E0ECC79A', '07/05/2019', 'Use serverTime to check canary expiration, instead of updateFormDigestPageLoaded');
        return isMagellan || useServerTimeExpirationCheckKillswitch;
    }

    private static _isXhrAborted(xhr: any): boolean {
        try {
            // status isn't necessarily valid unless the readyState is true.
            if (xhr.readyState === 4 && (typeof (xhr.status) === 'undefined' || xhr.status === 0)) {
                return true;
            }
            if (xhr.isCancelled) {
                return true;
            }
        } catch (e) {
            return true;
        }
        return false;
    }

    private static _isValidFormDigest(formDigest: IFormDigest): boolean {
        // Value of serverTime is same as what comes as part of formDigestValue
        // but is in locale neutral ISO 8601 format. So it will get correctly
        // parsed by Date class irrespective of client locale.
        // serverTime is accurate to the order of ms, while DateTime which comes
        // as part of formDigestValue is trimmed to order of seconds. Subtract
        // 60 s from formDigestTimeoutSeconds to avoid any timing errors b/w
        // server and client.
        if (ServerConnection._useServerTimeExpirationCheck()) {
            return formDigest && formDigest.serverTime &&
                (Date.now() - new Date(formDigest.serverTime).getTime()) < (formDigest.formDigestTimeoutSeconds - CLIENT_SERVER_BUFFER) * 1000 /*convert to ms*/;
        } else {
            return formDigest && formDigest.updateFormDigestPageLoaded &&
                (Date.now() - formDigest.updateFormDigestPageLoaded.getTime()) < formDigest.formDigestTimeoutSeconds * 1000 /*convert to ms*/;
        }
    }

    constructor(params: IServerConnectionParams = {}) {
        this._scope = new Scope();

        this._events = this._scope.attach(new EventGroup(this));

        // If the absolute URL is provided, it will be used for most requests.
        let webAbsoluteUrl = params.webAbsoluteUrl || params.webUrl;
        // The server-relative URL is used for caching request digests.
        // If it's not provided, try and calculate it from the absolute URL.
        this._webServerRelativeUrl = params.webServerRelativeUrl || (webAbsoluteUrl && new Uri(webAbsoluteUrl).getPath());
        if (this._webServerRelativeUrl === '/') {
            this._webServerRelativeUrl = '';
        }

        if (webAbsoluteUrl) {
            this._webAbsoluteUrl = webAbsoluteUrl;
            this._alwaysGetDigest = true;
        } else {
            this._alwaysGetDigest = false;
        }

        this._needsRequestDigest = params.needsRequestDigest !== false;
        if (this._needsRequestDigest) {
            this._tryLoadDigest(params.formDigest);
        }
        this._requestCanaryForAuth = false;
        this._engagement = params.engagement;
        this._operationName = params.operationName;
    }

    public dispose(): void {
        this._scope.dispose();
    }

    public abort(): void {
        if (this._currentRequest) {
            // Add a custom field to this request object so we can pass the message to the OnReadyStateChange function that THIS specific XHR has been canceled!
            //  It's unclear to me is this is really needed.  Some documentation I have ready states 'Calling abort resets the object; the onreadystatechange
            //  event handler is removed, and readyState is changed to 0 (uninitialized).'  If this is the case, then the onreadystatechange should never be
            //  called and if it is then the state of the request is set to 0, which I handle below.
            // For completeness only I leave this here.  (Belt and suspenders).
            let request = this._currentRequest;
            request.isCancelled = true;

            // Now abort this XHR
            request.abort();
            this._currentRequest = undefined;
        }
    }

    public isRequestActive(): boolean {
        return !!this._currentRequest;
    }

    public getServerDataFromUrl(options: IGetServerDataFromUrlOptions): void {
        let {
            url: strUrl,
            successCallback,
            failureCallback,
            uploadProgressCallback,
            additionalPostData,
            isRest: fRest = true,
            method = 'POST',
            withCredentials,
            additionalHeaders,
            contentType,
            noRedirect,
            responseType,
            needsRequestDigest = this._needsRequestDigest
        } = options;

        const startTime = new Date().toISOString();

        const req: ExtendedXHR = new XMLHttpRequest();

        req.open(method, strUrl, true);

        if (typeof withCredentials === 'boolean') {
            req.withCredentials = withCredentials;
        }

        if (responseType) {
            req.responseType = <any>responseType;
        }

        // Set the Content Type
        if (contentType) {
            req.setRequestHeader('Content-Type', contentType);
        } else {
            if (fRest) {
                req.setRequestHeader('Content-Type', 'application/json;odata=verbose');
            } else {
                req.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
            }
        }

        let acceptHeaderSpecified: boolean = false;
        if (additionalHeaders) {
            for (let headerKey in additionalHeaders) {
                if (headerKey) {
                    req.setRequestHeader(headerKey, additionalHeaders[headerKey]);
                    if (headerKey.toLowerCase() === 'accept') {
                        acceptHeaderSpecified = true;
                    }
                }
            }
        }
        if (!acceptHeaderSpecified) {
            req.setRequestHeader('accept', 'application/json;odata=verbose');
        }

        // Remember this request so we can tell if we have a request in flight and so we can cancel it if needed.
        this._currentRequest = req;
        const onRequestDigestReady = (requestDigest?: string): void => {
            if (needsRequestDigest && requestDigest) {
                req.setRequestHeader('x-requestdigest', requestDigest);
            }

            this._events.on(req, 'readystatechange', () => {
                this._onReadyStateChange(req, strUrl, successCallback, failureCallback, noRedirect, startTime);
            });

            if (uploadProgressCallback && req.upload) {
                this._events.on(req.upload, 'progress', (event: ProgressEvent) => {
                    uploadProgressCallback(event);
                });
            }

            req.send(additionalPostData);
        };

        if (needsRequestDigest) {
            this._ensureRequestDigest(onRequestDigestReady, failureCallback);
        } else {
            onRequestDigestReady();
        }
    }

    public ensureRequestDigest(callback: (requestDigest: string) => any, failureCallback?: IFailureCallback): void {
        this._ensureRequestDigest(callback, failureCallback);
    }

    private _ensureRequestDigest(callback: (requestDigest: string) => void, failureCallback?: IFailureCallback): void {
        // if requestDigest is available and not expired then use it, otherwise get a new one
        let currentDigest = ServerConnection._formDigests[this._webServerRelativeUrl];
        if (!this._alwaysGetDigest && ServerConnection._isValidFormDigest(currentDigest)) {
            callback(currentDigest.formDigestValue);
        } else {
            this._ensureRequestDigestWorker(callback, failureCallback);
        }
    }

    private _ensureRequestDigestWorker(callback?: (requestDigest: string) => void, failureCallback?: IFailureCallback): void {
        let formDigestRequestTime: Date = new Date();
        let serverConnection = new ServerConnection({
            needsRequestDigest: false,
            webServerRelativeUrl: this._webServerRelativeUrl,
            webUrl: undefined
        });

        if (!callback) {
            // no callback only means we're doing auth only request.
            serverConnection._requestCanaryForAuth = true;
        }

        let webUrl = this._webAbsoluteUrl || this._webServerRelativeUrl;
        if (typeof webUrl === 'string') {
            // If static promise does not exist, create it
            if (!ServerConnection._requestDigestPromise) {
                ServerConnection._requestDigestPromise = new Promise<string>(
                    (complete: (result?: string) => void, e: (error?: any) => void) => {
                        let onDataSuccess = (serverData: ServerData) => {
                            const responseText = serverData.getResponseText();
                            const jsonObj = JSON.parse(<string>responseText);
                            const requestDigest = jsonObj.d.GetContextWebInformation;

                            ServerConnection._formDigests[this._webServerRelativeUrl] = {
                                serverTime: formDigestRequestTime.toISOString(),
                                formDigestValue: requestDigest.FormDigestValue,
                                formDigestTimeoutSeconds: requestDigest.FormDigestTimeoutSeconds - CLIENT_SERVER_BUFFER,
                                updateFormDigestPageLoaded: new Date()
                            };

                            complete(ServerConnection._formDigests[this._webServerRelativeUrl].formDigestValue);
                            ServerConnection._requestDigestPromise = undefined;
                        };

                        let onDataError = (serverData: ServerData) => {
                            if (callback) {
                                callback(undefined);
                            } else if (failureCallback) {
                                failureCallback(serverData);
                            }
                            e(undefined);
                            ServerConnection._requestDigestPromise = undefined;
                        };

                        serverConnection.getServerDataFromUrl({
                            url: Uri.concatenate(webUrl, '/_api/contextinfo'),
                            successCallback: onDataSuccess,
                            failureCallback: onDataError,
                            method: 'POST',
                            noRedirect: !!failureCallback
                        });
                    }
                );
            }

            ServerConnection._requestDigestPromise.then((requestDigest) => {
                if (callback) {
                    callback(requestDigest);
                }
                return requestDigest;
            });
        } else if (failureCallback) {
            failureCallback(undefined, new ApiError({
                code: 'MissingWebForDigestAuth',
                isExpected: false
            }));
        }
    }

    private _onReadyStateChange(
        req: XMLHttpRequest,
        strUrl: string,
        successCallback: (ev: any) => any,
        failureCallback: IFailureCallback,
        noRedirect: boolean,
        startTime: string) {
        // Don't do anything unless the State is 'Ready' and this request has not yet been aborted.
        if (req.readyState !== 4) {
            return;
        }

        // undefined out the request that this object is holding onto.  This is the flag to let us know that we no longer have an active request.
        this._currentRequest = undefined;

        this._events.off(req);

        if (req.upload) {
            this._events.off(req.upload);
        }

        if (!ServerConnection._isXhrAborted(req)) {
            let serverData = new ServerData(req, strUrl, {
                webAbsoluteUrl: this._webAbsoluteUrl || this._webServerRelativeUrl,
                isCanaryRequest: this._requestCanaryForAuth
            });
            let status = serverData.getStatus();

            let rumOne = RUMOneLogger.getRUMOneLogger();
            let apiEndTime = Date.now();
            if (rumOne) {
                let apiData: APICallPerformanceData = new APICallPerformanceData(strUrl, Number(req.getResponseHeader('SPClientServiceRequestDuration')), req.getResponseHeader('SPRequestGuid'), status, startTime, new Date().toISOString());
                rumOne.writeAPICallPerformanceData(apiData);   // log a API call perf data object for each API call before EUPL complete
                rumOne.saveTempData('appDataFetchEnd', apiEndTime);  // Record the last API call end time as app data fetch end
            }

            const shouldRedirectToErrorCustomPromptLocation = !noRedirect && serverData.shouldRedirectToErrorCustomPromptLocation();

            if (this._requestCanaryForAuth) {
                this._requestCanaryForAuth = false;
                if (status === 403) {
                    let redirectUrl = serverData.getRedirectLoginPageUrl();

                    try {
                        if (shouldRedirectToErrorCustomPromptLocation) {
                            window.location.href = serverData.getRedirectToErrorCustomPromptLocation();
                        } else {
                            failureCallback(serverData);
                        }
                    } catch (e) {
                        // ignore
                    }

                    if (redirectUrl && !noRedirect) {
                        window.location.href = redirectUrl;
                    }
                } else {
                    let redirectUrl = serverData.getAccessDeniedRedirectUrl();

                    // we got form digest, so user is authenticated, they just don't have access to call the API requested
                    // let data source know about the error so it get properly accounted
                    try {
                        failureCallback(serverData);
                    } catch (e) {
                        // ignore
                    }

                    if (redirectUrl && !noRedirect) {
                        if (this._engagement) {
                            this._engagement.logData({
                                name: "AccessDeniedRedirect",
                                isIntentional: false,
                                extraData: {
                                    originalOperation: this._operationName || ''
                                }
                            });
                        }
                        window.location.href = redirectUrl;
                    }
                }
                return;
            }

            if (status === 200 || status === 201 || status === 204) { // success case
                successCallback(serverData);
                return;
            } else if (status === 403) { // authentication required
                ServerConnection._requestDigestPromise = undefined;
                if (noRedirect) {
                    this._ensureRequestDigestWorker(undefined, () => failureCallback(serverData)); // call Canary to check if the user has already signed in or ot.
                } else {
                    if (shouldRedirectToErrorCustomPromptLocation) {
                        window.location.href = serverData.getRedirectToErrorCustomPromptLocation();
                    } else {
                        this._ensureRequestDigestWorker(undefined);
                    }
                }
                return;
            } else if (status === 401) {
                if (shouldRedirectToErrorCustomPromptLocation) {
                    window.location.href = serverData.getRedirectToErrorCustomPromptLocation();
                }
            }
            failureCallback(serverData);
        } else {
            failureCallback(undefined, new ApiError({
                code: 'RequestAborted',
                isExpected: true
            }));
        }
    }

    /** Attempts to load or refresh the cached form digest with the params form digest data. */
    private _tryLoadDigest(formDigest: IFormDigest) {
        if (ServerConnection._isValidFormDigest(formDigest) && !Killswitch.isActivated('0BD8A6FF-5CA5-45F8-BE49-C60C2C417F38', '07/08/2019', 'Refresh cached form digest the moment we get a valid one from params')) {
            ServerConnection._formDigests[this._webServerRelativeUrl] = formDigest;
            return;
        }
        // should start using the params data when:
        // params has valid form digest data and
        // - the cached form digest data is invalid or expired or
        // - the params digest data is fresher that the cached data.
        const cachedFormDigest = ServerConnection._formDigests[this._webServerRelativeUrl];
        if (ServerConnection._isValidFormDigest(formDigest) &&
            (!ServerConnection._isValidFormDigest(cachedFormDigest) ||
                (cachedFormDigest &&
                    (ServerConnection._useServerTimeExpirationCheck() ?
                        new Date(cachedFormDigest.serverTime).getTime() < new Date(formDigest.serverTime).getTime() :
                        cachedFormDigest.updateFormDigestPageLoaded.getTime() < formDigest.updateFormDigestPageLoaded.getTime()
                    )
                )
            )
        ) {
            ServerConnection._formDigests[this._webServerRelativeUrl] = formDigest;
        }
    }
}
